class DSCartDrawer extends HTMLElement {
  constructor() {
    super();
    this._lacProcessing = false;
    this._isOpen = false;
    this._pendingClose = false;
    this._inertElements = new Set();
  }

  get dialog() {
    return this.querySelector('[role="dialog"]');
  }

  connectedCallback() {
    this._events?.abort();
    this._events = new AbortController();
    const options = { signal: this._events.signal };
    this.inert = !this._isOpen;
    this.setAttribute('aria-hidden', String(!this._isOpen));

    document.addEventListener('liquid-ajax-cart:queue-start', () => {
      this._lacProcessing = true;
      this.dialog?.setAttribute('aria-busy', 'true');
    }, options);
    document.addEventListener('liquid-ajax-cart:queue-end', () => {
      this._lacProcessing = false;
      this._pendingClose = false;
      this.dialog?.setAttribute('aria-busy', 'false');
      if (this._isOpen) this.restoreUpdatedFocus();
      this._focusBeforeUpdate = null;
    }, options);
    document.addEventListener('liquid-ajax-cart:request-start', () => {
      const focused = this.contains(document.activeElement) ? document.activeElement : this._lastFocusedElement;
      if (!this._isOpen || !this.contains(focused)) return;
      const quantityInput = focused.closest('.ds-quantity')?.querySelector('input[id]');
      this._focusBeforeUpdate = {
        id: focused.id || quantityInput?.id,
        button: focused.matches('button[name]') ? focused.name : null
      };
    }, options);
    document.addEventListener('liquid-ajax-cart:request-end', (event) => {
      const { requestState, cart } = event.detail;
      const isAdd = requestState.requestType === 'add' && requestState.responseData?.ok;
      this.updateCartEmptyState(cart);
      if (isAdd && !this._isOpen && !this._pendingClose) this.open();
      if (this._isOpen) {
        if (!this._lacProcessing) this.restoreUpdatedFocus();
        const status = this.querySelector('[data-cart-a11y-status]');
        if (status && requestState.responseData?.ok && cart) {
          status.textContent = status.dataset.updatedMessage.replace('[count]', cart.item_count);
        }
      }
      if (!this._lacProcessing) this._focusBeforeUpdate = null;
    }, options);
    document.addEventListener('keydown', (event) => this.handleKeyDown(event), options);
    document.addEventListener('focusin', (event) => {
      if (this.contains(event.target)) {
        this._lastFocusedElement = event.target;
        return;
      }
      if (!this._isOpen || this.inert || this._lacProcessing || window.liquidAjaxCart?.processing || this.hasOtherDialog(event.target)) return;
      this.focusInitialElement();
    }, options);
    this.addEventListener('click', (event) => {
      if (event.target.matches('#CartDrawer-Overlay')) this.close();
    }, options);
    this.setHeaderCartIconAccessibility(options);

    if (window.liquidAjaxCart?.cart) this.updateCartEmptyState(window.liquidAjaxCart.cart);
    if (!this._openedFromUrl && new URLSearchParams(window.location.search).get('open_cart') === '1') {
      this._openedFromUrl = true;
      this.open(document.querySelector('#ds-cart-icon-bubble'));
    }
  }

  disconnectedCallback() {
    this._events?.abort();
    clearTimeout(this._openTimer);
    clearTimeout(this._focusTimer);
    cancelAnimationFrame(this._focusFrame);
    this.restoreBackground();
    if (this._isOpen) document.body.classList.remove('overflow-hidden');
    this._isOpen = false;
    this._pendingClose = false;
  }

  updateCartEmptyState(cart) {
    const isEmpty = !cart || cart.item_count === 0;
    const emptyWrapper = this.querySelector('.ds-drawer__inner-empty');
    const cartDrawerItems = this.querySelector('cart-drawer-items');
    this.classList.toggle('is-empty', isEmpty);
    if (emptyWrapper) emptyWrapper.style.display = isEmpty ? '' : 'none';
    if (cartDrawerItems) {
      cartDrawerItems.classList.toggle('is-empty', isEmpty);
      cartDrawerItems.style.display = isEmpty ? 'none' : '';
    }
  }

  setHeaderCartIconAccessibility(options) {
    const cartLink = document.querySelector('#ds-cart-icon-bubble');
    if (!cartLink) return;
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.setAttribute('aria-controls', 'CartDrawer-Dialog');
    cartLink.setAttribute('aria-expanded', String(this._isOpen));
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    }, options);
    cartLink.addEventListener('keydown', (event) => {
      if (event.key !== ' ' && event.key !== 'Enter') return;
      event.preventDefault();
      this.open(cartLink);
    }, options);
  }

  open(triggeredBy) {
    if (this._isOpen || (this._pendingClose && !triggeredBy)) return;
    this._isOpen = true;
    this._pendingClose = false;
    this.setActiveElement(triggeredBy || document.activeElement);
    this._openTimer = setTimeout(() => {
      if (!this._isOpen || !this.isConnected) return;
      this.inert = false;
      this.setAttribute('aria-hidden', 'false');
      this.classList.add('animate', 'active');
      document.querySelector('#ds-cart-icon-bubble')?.setAttribute('aria-expanded', 'true');
      this.makeBackgroundInert();
      this._focusFrame = requestAnimationFrame(() => {
        if (!this._isOpen || !this.isConnected) return;
        this.focusInitialElement();
        document.dispatchEvent(new CustomEvent('ds-cart-drawer:opened'));
      });
      const duration = Math.max(...(getComputedStyle(this.dialog).transitionDuration || '0s').split(',').map(value => parseFloat(value) || 0));
      this._focusTimer = setTimeout(() => {
        if (this._isOpen && !this.dialog.contains(document.activeElement) && !this.hasOtherDialog(document.activeElement)) this.focusInitialElement();
      }, duration * 1000 + 50);
    });
    document.body.classList.add('overflow-hidden');
  }

  close() {
    if (!this._isOpen) return;
    clearTimeout(this._openTimer);
    clearTimeout(this._focusTimer);
    cancelAnimationFrame(this._focusFrame);
    this._pendingClose = this._lacProcessing;
    this._isOpen = false;
    this.classList.remove('active');
    document.body.classList.remove('overflow-hidden');
    this.restoreBackground();
    document.querySelector('#ds-cart-icon-bubble')?.setAttribute('aria-expanded', 'false');
    const target = this.activeElement?.isConnected && !this.contains(this.activeElement) &&
      this.activeElement.tabIndex >= 0 && !this.activeElement.matches(':disabled') &&
      !this.activeElement.closest('[inert]') && this.activeElement.getClientRects().length &&
      getComputedStyle(this.activeElement).visibility !== 'hidden'
      ? this.activeElement
      : document.querySelector('#ds-cart-icon-bubble');
    target?.focus({ preventScroll: true });
    this.inert = true;
    this.setAttribute('aria-hidden', 'true');
  }

  hasOtherDialog(target) {
    if (document.querySelector('dialog[open]')) return true;
    const modal = target?.closest('[role="dialog"][aria-modal="true"], [role="alertdialog"]');
    return !!modal && !this.contains(modal);
  }

  handleKeyDown(event) {
    if (!this._isOpen || event.defaultPrevented || event.isComposing || this.hasOtherDialog(event.target)) return;
    if (event.key === 'Escape' || event.code === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.close();
      return;
    }
    if (event.key !== 'Tab' || this.inert) return;
    const elements = this.focusableElements();
    const first = elements[0];
    const last = elements[elements.length - 1];
    const focused = document.activeElement;
    if (!first) {
      event.preventDefault();
      this.dialog?.focus({ preventScroll: true });
    } else if (event.shiftKey && (focused === first || focused === this.dialog || !this.dialog.contains(focused))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (focused === last || !this.dialog.contains(focused))) {
      event.preventDefault();
      first.focus();
    }
  }

  focusableElements() {
    return Array.from(this.dialog?.querySelectorAll('a[href], button, input, select, textarea, summary, [contenteditable], [tabindex]') || [])
      .filter((element) => element.tabIndex >= 0 && !element.matches(':disabled') &&
        !element.closest('[inert], [hidden], [aria-hidden="true"]') &&
        element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden');
  }

  focusInitialElement() {
    const target = this.querySelector('.ds-drawer__close') || this.dialog;
    target?.focus({ preventScroll: true });
  }

  restoreUpdatedFocus() {
    if (this.inert || this.contains(document.activeElement) || this.hasOtherDialog(document.activeElement)) return;
    const previous = this._focusBeforeUpdate;
    let target = previous?.id ? document.getElementById(previous.id) : null;
    if (target && previous.button) target = target.closest('.ds-quantity')?.querySelector(`button[name="${previous.button}"]`);
    if (target && this.contains(target) && this.focusableElements().includes(target)) target.focus({ preventScroll: true });
    else this.focusInitialElement();
  }

  makeBackgroundInert() {
    let branch = this;
    while (branch.parentElement && branch !== document.body) {
      for (const sibling of branch.parentElement.children) {
        if (sibling === branch || sibling.inert || sibling.hidden || sibling.matches('script, style, link, template')) continue;
        sibling.inert = true;
        this._inertElements.add(sibling);
      }
      branch = branch.parentElement;
    }
  }

  restoreBackground() {
    this._inertElements.forEach((element) => { element.inert = false; });
    this._inertElements.clear();
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner')?.classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (sectionElement) {
        sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      }
    });
    setTimeout(() => this.open());
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [{ id: 'cart-drawer', selector: '#CartDrawer' }];
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

class CartDrawerItems extends HTMLElement {}

customElements.define('ds-cart-drawer', DSCartDrawer);
customElements.define('cart-drawer-items', CartDrawerItems);
